/*
 * Shared filter vocabulary for the Sales Task List feature — the option lists,
 * the defaults, the localStorage-backed initial state and the query string the
 * API expects.
 *
 * Extracted from index.jsx (the list page) when the task form started needing the
 * same filters: "Done and Next" asks the server for the next task, and that walk
 * has to stay inside the set the agent was browsing — otherwise a concierge
 * filtered to their own list is handed somebody else's task. Both pages build the
 * query here so the two can never drift.
 *
 * The persistence layer itself lives in filters_storage.js; this module owns what
 * the values MEAN (defaults, option lists, wire format).
 */

import moment from 'moment-timezone';
import { read_persisted_filters } from './filters_storage';

// Sentinel "Assigned to" option value for tasks with no assignee. A real
// cms_user uuid never collides with it. Mirrors unassignedFilterValue in
// goserver's api_list.go.
export const UNASSIGNED_VALUE = '__unassigned__';

// The backend reads date_from / date_to as IST calendar days (it converts them
// to UTC instants with the IST helpers), so the defaults must be computed in IST
// rather than in the viewer's timezone.
const IST_TZ = 'Asia/Kolkata';

// Default due window: yesterday → tomorrow in IST, i.e. the three days a sales
// agent actually works — what slipped, what's due now, what's next.
export const default_date_range = () => ({
  from: moment().tz(IST_TZ).subtract(1, 'day').format('YYYY-MM-DD'),
  to: moment().tz(IST_TZ).add(1, 'day').format('YYYY-MM-DD')
});

// Default "Assigned to" selection: unassigned tasks plus the logged-in agent's
// own. The list API OR-combines the two (see unassignedFilterValue in
// api_list.go), so this is one query, not two.
export const default_agent_filter = (self_uuid) =>
  self_uuid ? [UNASSIGNED_VALUE, self_uuid] : [UNASSIGNED_VALUE];

// Status filter tabs. Sales tasks have no draft stage in practice — just
// pending → submitted (completed) — though the status column can hold 'draft',
// and the server folds that into "Pending" ("not submitted").
//
// "Overdue" is not a stored status: it is pending AND past due_datetime,
// evaluated server-side against UTC now (see statusClause in db.go). It sits
// second because it is the subset of Pending that needs attention first.
export const STATUS_FILTERS = [
  { label: 'All', value: 'All' },
  { label: 'Overdue', value: 'Overdue' },
  { label: 'Pending', value: 'Pending' },
  { label: 'Submitted', value: 'Submitted' }
];

// Task-type filter options, in the same order as the config cards. There are
// eleven of them, which is why this is a multi-select dropdown rather than the
// pill bar it started as — the bar overflowed the filter row. An EMPTY selection
// means "all types" (the same convention the assignee dropdown uses), so there is
// no 'All' option to pick.
export const TYPE_OPTIONS = [
  { label: 'Fresh lead', value: 'FRESH_LEAD' },
  { label: 'Upcoming trial session', value: 'UPCOMING_TRIAL_SESSION' },
  { label: 'Unanswered call', value: 'UNANSWERED_INCOMING_CALL' },
  { label: 'Unanswered SMS', value: 'UNANSWERED_INCOMING_SMS' },
  { label: 'Pricing page visited', value: 'PRICING_PAGE_VISITED' },
  { label: '3 month plan opened', value: 'THREE_MONTH_PLAN_OPENED' },
  { label: 'No book', value: 'NO_BOOK' },
  { label: 'Book, no join', value: 'BOOK_NO_JOIN' },
  { label: 'Join, no membership', value: 'JOIN_NO_MEMBERSHIP' },
  { label: 'No trial reset', value: 'LOGIN_NO_TRIAL_RESET' },
  { label: 'Reset, no book', value: 'TRIAL_RESET_NO_BOOK' },
  { label: 'Reset, booked, no join', value: 'TRIAL_RESET_BOOK_NO_JOIN' },
  { label: 'Reset, joined, no membership', value: 'TRIAL_RESET_JOIN_NO_MEMBERSHIP' }
];

export const STATUS_VALUES = STATUS_FILTERS.map((o) => o.value);
export const TYPE_VALUES = TYPE_OPTIONS.map((o) => o.value);

/*
 * Initial state of the four persisted filters. Saved choices win over the
 * built-in defaults — including a deliberately cleared one, which is stored as an
 * empty selection and must not be re-seeded. A filter that was never saved, or
 * whose saved value no longer validates, falls back to its default individually,
 * so one stale value can't discard the rest.
 *
 * The date range is the exception: it is restored only when the user explicitly
 * picked or cleared it (date_range non-null in storage). Left untouched, it
 * re-seeds from today so the window keeps rolling instead of restoring last
 * week's absolute dates.
 *
 * The list page holds this in component state; the form page resolves it fresh
 * (read-only) to reproduce the filters the agent was browsing with.
 */
export const initial_filter_state = (self_uuid) => {
  const rolling = default_date_range();
  const state = {
    status_filter: 'Pending',
    // [] = every task type. Multi-select, like agent_filter.
    type_filter: [],
    agent_filter: default_agent_filter(self_uuid),
    date_from: rolling.from,
    date_to: rolling.to,
    // False while the range is still the rolling default; only an explicit pick
    // or clear is persisted (see filters_storage.js).
    date_range_touched: false
  };

  const saved = read_persisted_filters({
    status_values: STATUS_VALUES,
    type_values: TYPE_VALUES
  });
  if (!saved) return state;

  if (saved.status_filter !== undefined) state.status_filter = saved.status_filter;
  if (saved.type_filter !== undefined) state.type_filter = saved.type_filter;
  if (saved.agent_filter !== undefined) state.agent_filter = saved.agent_filter;
  if (saved.date_range) {
    state.date_from = saved.date_range.from;
    state.date_to = saved.date_range.to;
    state.date_range_touched = true;
  }
  return state;
};

/*
 * Serialise the filters into the query params both /list and /next_task read
 * (status, task_type, date_from, date_to, assigned, search). Sorting and
 * pagination are list-only and stay with the caller — the same split the server
 * makes in listFiltersFromQuery.
 *
 * Omitting a param means "no filter on it", which is why the empty selections are
 * simply left out rather than sent as an explicit "All".
 *
 * search is the type-to-search box on the list page: a prefix match on the
 * student's first name, resolved server-side (the table is server-paginated, so
 * a client-side filter would only ever search the current 20-row page). It is
 * NOT persisted to localStorage — a search is momentary, not a working
 * preference — so unlike the other four filters it cannot reach the task form
 * through storage. The list instead puts an active search term in the "Open Task"
 * href (?search=…) and the form reads it back, so "Done and Next" walks the
 * searched set rather than silently widening to the unsearched one.
 */
export const filter_query_params = ({
  status_filter,
  type_filter = [],
  date_from,
  date_to,
  agent_filter = [],
  search = ''
} = {}) => {
  const params = new URLSearchParams();
  if (status_filter) params.set('status', status_filter.toLowerCase());
  // Comma-separated list; the API also accepts a repeated param and a single
  // bare value (the pre-multi-select form).
  if (type_filter.length) params.set('task_type', type_filter.join(','));
  if (date_from) params.set('date_from', date_from);
  if (date_to) params.set('date_to', date_to);
  if (agent_filter.length) params.set('assigned', agent_filter.join(','));
  // The server trims and LIKE-escapes it; trimming here too keeps a stray space
  // from making the request look filtered when it isn't.
  if (search && search.trim()) params.set('search', search.trim());
  return params;
};
